import * as React from "react";
import { Button } from "antd";
import { CloseOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { useTableKindId } from "../../../newcomponents/table/data/useTableKindId";
import { useTableItemQueryData } from "../../../newcomponents/table/data/useTableItemQueryData";
import { parseLink } from "../../../utils/linkParser/linkParser";
import { CardContent } from "../../../components/antd/card/CardContent";
import { Table } from "../../../newcomponents/table/table";
import { NameDescriptionColumn } from "../../../newcomponents/table/columns/wellKnown/nameDescriptionColumn";
import { TagsColumn } from "../../../newcomponents/table/columns/wellKnown/tagsColumn";
import { CreatedColumn } from "../../../newcomponents/table/columns/wellKnown/createdColumn";
import { LastModifiedColumn } from "../../../newcomponents/table/columns/wellKnown/lastModifiedColumn";
import { TableModal } from "../../../components/antd/TableModal";
import { TableHeaderRefreshButton } from "../../../newcomponents/table/components/RefreshButton";
import { CustomTableCPLNQuery } from "../../../newcomponents/table/components/TableCplnQuery";
import { NameDescriptionNoLinkColumn } from "../../../newcomponents/table/columns/wellKnown/nameDescriptionNoLinkColumn";
import { Secret } from "../../../schema/types/secret/entity";
import { SecretTypeColumn } from "../../../newcomponents/table/columns/secret/typeColumn";
import { PolicyInput } from "../../../schema/types/policy/input";
import { getSecretTableItem, SecretTableItem } from "../../../mst/kinds/secret.helpers";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../gvc/testNewForm/utils";
import { store } from "../../../store";
import { cloneDeep } from "lodash";
import { KindSelectState } from "../../../components/antd/kindselect/KindSelect";

interface Props {}
export const PolicyTargetSecret: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();

  const { kind: addTableKind, id: addTableId } = useTableKindId("secret", "policy-target-items-secret");
  const qData = useTableItemQueryData<Secret>(addTableKind, {});

  const [selectionsToAdd, setSelectionsToAdd] = React.useState<string[]>([]);
  const [selectionItems, setSelectionItems] = React.useState<SecretTableItem[]>([]);
  const [itemCache, setItemCache] = React.useState<Record<string, SecretTableItem>>({});
  const [isAddingItem, setIsAddingItem] = React.useState<boolean>(false);

  const secrets = useCpFormValue(`targets`);
  const secretTableItems = React.useMemo(() => secrets.map((s) => getSecretTableItem(s)), [secrets]);

  const onRemoveItem = async (index: number) => {
    const form = store.getState().form.instances[id];
    const values: PolicyInput = form.values as any;

    const currentItems: KindSelectState[] = cloneDeep(values.targets);
    currentItems.splice(index, 1);
    formSetValue(id, `targets`, currentItems);

    formTriggerValidation(id);
  };

  function onOkAddModal() {
    const form = store.getState().form.instances[id];
    const values: PolicyInput = form.values as any;

    const currentItems: KindSelectState[] = cloneDeep(values.targets);

    // TODO selectionsToAdd can be renamed to something with link
    for (const selectionToAdd of selectionsToAdd) {
      const currentLinks = currentItems.map((u) => u.link);
      const item = selectionItems.find((i) => i.selfLink === selectionToAdd);
      if (item) {
        itemCache[selectionToAdd] = item;
      }

      if (currentLinks.includes(selectionToAdd)) {
        continue;
      }

      const { name } = parseLink(selectionToAdd, { useInputCtx: true });
      currentItems.push({
        value: name,
        item: item,
        link: selectionToAdd,
      });
    }
    // TODO was sorting the links but no more because now it is kindselectstate

    // TODO initialize this item cache on load for detail page
    setItemCache(itemCache);

    formSetValue(id, `targets`, currentItems);

    setSelectionsToAdd([]);
    setSelectionItems([]);
    setIsAddingItem(false);

    formTriggerValidation(id);
  }

  const currentLinks = secrets.map((t) => t.link);
  const addSecretTableItems = qData.visibleItems
    .map((item) => getSecretTableItem({ item: item }))
    .filter((item) => !currentLinks.includes(item.selfLink));

  return (
    <>
      <CardContent>
        {secretTableItems.length > 0 ? (
          <Table<SecretTableItem>
            tableId="policy-target-items-secret-editable"
            data={secretTableItems}
            hideSettings
            columns={[
              NameDescriptionNoLinkColumn({ enableSort: false }),
              SecretTypeColumn(),
              TagsColumn(),
              CreatedColumn(),
              LastModifiedColumn(),
              {
                id: "action",
                label: "",
                enableResize: false,
                size: 120,
                cell: ({ row }) => {
                  return (
                    <div className="w-full flex justify-end">
                      <Button
                        htmlType={`button`}
                        type={"text"}
                        icon={<CloseOutlined />}
                        danger
                        onClick={() => onRemoveItem(row.index)}
                      />
                    </div>
                  );
                },
              },
            ]}
          />
        ) : null}
        <TableModal
          open={isAddingItem}
          onCancel={() => setIsAddingItem(false)}
          onOk={() => {
            onOkAddModal();
          }}
        >
          <Table<SecretTableItem>
            tableId={addTableId}
            title={"Select Secrets to Add"}
            selectMode="multi"
            selectKey="selfLink"
            selections={selectionsToAdd}
            onSelectionsChange={(keys, items) => {
              setSelectionsToAdd(keys);
              setSelectionItems(items!);
            }}
            data={addSecretTableItems}
            headerRenderer={() => {
              return (
                <>
                  <TableHeaderRefreshButton onClick={qData.fetchItems} />
                  <CustomTableCPLNQuery
                    tableId={addTableId}
                    kind={addTableKind}
                    onQuery={qData.setQuery}
                    isLoading={qData.isLoadingQueried}
                  />
                </>
              );
            }}
            enableFilter
            isLoading={qData.isLoading}
            onSortChange={qData.setSort}
            onSearchChange={qData.setSearch}
            isLoadingMore={qData.isLoadingMore}
            hasMore={qData.hasMore}
            onLoadMore={qData.fetchNextPage}
            columns={[
              NameDescriptionColumn(undefined, { shouldForceTargetBlank: () => true }),
              SecretTypeColumn(),
              TagsColumn(),
              CreatedColumn(),
              LastModifiedColumn(),
            ]}
          />
        </TableModal>
        <Button
          htmlType={`button`}
          className="mt-4"
          type={"dashed"}
          icon={<PlusCircleOutlined />}
          onClick={() => setIsAddingItem(true)}
        >
          Add Secret
        </Button>
      </CardContent>
    </>
  );
};
